import { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import countryList from "react-select-country-list";
import { CircleFlag } from "react-circle-flags";
import { countryMatchesSearch, toIso3 } from "../../utils/iso2to3";

import "./css/CountrySelect.css";

const noAdditionalOptions = [];

function buildCountryOptions(additionalOptions) {
  const baseOptions = [...countryList().getData()];
  const existingCodes = new Set(baseOptions.map(option => option.value));
  baseOptions.push(...additionalOptions.filter(option => !existingCodes.has(option.value)));
  if (additionalOptions.length) baseOptions.sort((a, b) => a.label.localeCompare(b.label));
  return baseOptions;
}

function filterCountryOption(option, rawInput) {
  return countryMatchesSearch(option?.data, rawInput);
}

function InputWithDescription(props) {
  const describedBy = [props["aria-describedby"], props.selectProps["aria-describedby"]]
    .filter(Boolean)
    .join(" ");
  return <components.Input {...props} aria-describedby={describedBy || undefined} />;
}

function OptionWithFlag(props) {
  const code = (props.data.value || "").toLowerCase(); // "AE" -> "ae"
  const cls = props.selectProps.optionTextClassName || "";
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CircleFlag className="flagMutedImg" countryCode={code} height="18" />
        <span className={`optionInsideCountrySelect ${cls}`}>
          {props.data.label}
        </span>
        {/* <span className="optionInsideCountrySelect">{props.data.label}</span> */}
      </div>
    </components.Option>
  );
}

function SingleValueWithFlag(props) {
  const code = (props.data.value || "").toLowerCase();
  const mode = props.selectProps.singleValueMode || "label";
  const text =
    mode === "code"
      ? String(props.data.value || "").toUpperCase()
      : mode === "iso3"
      ? toIso3(props.data.value) || String(props.data.value || "").toUpperCase()
      : props.data.label;
  if (props.selectProps.showSelectedFlag === false) {
    return <components.SingleValue {...props}>{text}</components.SingleValue>;
  }
  return (
    <components.SingleValue {...props} innerProps={{ ...props.innerProps, title: props.data.label }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <CircleFlag className="flagMutedImg" countryCode={code} height="18" style={{ flexShrink: 0 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{text}</span>
      </div>
    </components.SingleValue>
  );
}

export default function CountrySelect({
  value,
  onChange,
  optionTextClassName,
  className,
  disabled = false,
  singleValueMode = "label",
  menuMinWidth,
  menuPortalZIndex = 5000,
  optionFontSize,
  placeholder,
  clearOnDelete = true,
  autoFocus = false,
  defaultMenuOpen = false,
  inputId,
  ariaLabel,
  ariaDescribedBy,
  menuPortalTarget = document.body,
  preventAutoFocusScroll = false,
  menuAnchorRef,
  showSelectedFlag = true,
  capitalizeFirstLetter = false,
  additionalOptions = noAdditionalOptions,
  // Picker: the list stays open below the search field, in the page flow (no floating menu).
  inlineMenu = false,
  maxMenuHeight,
  menuPlacement,
  minMenuHeight,
}) {
  const options = useMemo(() => buildCountryOptions(additionalOptions), [additionalOptions]);
  const selected = options.find((o) => o.value === value) || null;
  const selectRef = useRef(null);
  const [menuIsOpen, setMenuIsOpen] = useState(defaultMenuOpen);

  useEffect(() => {
    if (autoFocus && preventAutoFocusScroll) {
      selectRef.current?.inputRef?.focus({ preventScroll: true });
    }
  }, [autoFocus, preventAutoFocusScroll]);

  function handleKeyDown(event) {
    if (
      !clearOnDelete ||
      disabled ||
      event.key !== "Backspace" ||
      !selected
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onChange("");
    setMenuIsOpen(false);
    selectRef.current?.blur?.();
  }

  return (
    <Select
      ref={selectRef}
      autoFocus={autoFocus && !preventAutoFocusScroll}
      inputId={inputId}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      options={options}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : "")}
      components={{
        ...(ariaDescribedBy ? { Input: InputWithDescription } : {}),
        Option: OptionWithFlag,
        SingleValue: SingleValueWithFlag,
      }}
      isDisabled={disabled}
      className={className}
      classNamePrefix="rs"
      placeholder={placeholder !== undefined ? placeholder : "Select..."}
      filterOption={filterCountryOption}
      onInputChange={input => capitalizeFirstLetter
        ? input.replace(/^(\s*)(\p{L})/u, (_, spaces, letter) => spaces + letter.toUpperCase())
        : input}
      onKeyDown={handleKeyDown}
      menuIsOpen={inlineMenu || menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      menuPortalTarget={inlineMenu ? null : menuPortalTarget}
      menuPosition={inlineMenu ? "absolute" : "fixed"}
      maxMenuHeight={maxMenuHeight}
      menuPlacement={menuPlacement}
      minMenuHeight={minMenuHeight}
      menuShouldScrollIntoView={!inlineMenu}
      styles={{
        menuPortal: (base) => {
          const anchor = menuAnchorRef?.current?.getBoundingClientRect();
          return {
            ...base,
            zIndex: menuPortalZIndex,
            ...(anchor ? { left: anchor.left, top: anchor.bottom + 8, width: anchor.width } : {}),
          };
        },
        menu: (base) => ({
          ...base,
          minWidth: menuMinWidth || base.minWidth,
          ...(menuAnchorRef ? { marginTop: 0 } : {}),
          ...(inlineMenu ? { position: "relative", marginTop: 8, marginBottom: 0, boxShadow: "none", border: "1px solid #e3e8ec", borderRadius: 12, overflow: "hidden" } : {}),
        }),
        option: (base) => ({
          ...base,
          fontSize: optionFontSize || base.fontSize,
        }),
      }}
      optionTextClassName={optionTextClassName}
      singleValueMode={singleValueMode}
      showSelectedFlag={showSelectedFlag}
    />
  );
}
